import { useEffect, useState } from 'react';
import _ from 'lodash';
import { ERelativeTimeTypes } from '@timeedit/types/lib/enums';
import { TEInput } from '@timeedit/ui-components';
import { InputNumber, Select, Space } from '@timeedit/ui-components/antd';
import {
  ONE_DAY,
  ONE_HOUR,
  ONE_MINUTE,
} from '../../constants/DateLimits.constants';

interface IRelativeTimeProps {
  label: string;
  tooltip: string;
  flow?: 'row' | 'column';
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  dataTestId?: string;
  daysOnly?: boolean;
  status?: 'error' | 'warning';
}
interface IRelativeTime {
  time: number;
  interval: ERelativeTimeTypes;
}

const RelativeTime = ({
  label,
  tooltip,
  value,
  onChange,
  flow = 'row',
  disabled = false,
  dataTestId,
  daysOnly,
  status,
}: IRelativeTimeProps) => {
  const [relativeTime, setRelativeTime] = useState<IRelativeTime>({
    time: 0,
    interval: ERelativeTimeTypes.DAYS,
  });
  useEffect(() => {
    if (daysOnly) {
      return setRelativeTime({
        time: Math.round(value / ONE_DAY),
        interval: ERelativeTimeTypes.DAYS,
      });
    }
    const absVal = Math.abs(value);
    if (absVal === 0) {
      return setRelativeTime({
        time: 0,
        interval: relativeTime.interval,
      });
    }
    if (absVal >= ONE_DAY) {
      return setRelativeTime({
        time: Math.round(value / ONE_DAY),
        interval: ERelativeTimeTypes.DAYS,
      });
    }
    if (absVal >= ONE_HOUR) {
      return setRelativeTime({
        time: Math.round(value / ONE_HOUR),
        interval: ERelativeTimeTypes.HOURS,
      });
    }
    setRelativeTime({
      time: Math.round(value / ONE_MINUTE),
      interval: ERelativeTimeTypes.MINUTES,
    });
  }, [value, relativeTime.interval]);

  const handleChange = (time: number, interval: ERelativeTimeTypes) => {
    const sign = time < 0 ? -1 : 1;
    if (interval === ERelativeTimeTypes.HOURS && Math.abs(time) >= 24) {
      time = sign * 23;
    }
    if (interval === ERelativeTimeTypes.MINUTES && Math.abs(time) >= 60) {
      time = sign * 59;
    }
    onChange(time * intervalMap[interval]);
  };

  return (
    <TEInput.LabelWrapper label={label} tooltip={tooltip} flow={flow}>
      <Space data-testid={dataTestId}>
        <InputNumber
          value={relativeTime.time}
          size="small"
          status={status}
          onChange={(val) => {
            handleChange(val ?? 0, relativeTime.interval);
          }}
          disabled={disabled}
          data-testid="value"
        />
        <Select
          options={intervalOptions}
          value={relativeTime.interval}
          onChange={(interval) => {
            handleChange(relativeTime.time, interval);
          }}
          size="small"
          popupMatchSelectWidth={false}
          disabled={disabled || daysOnly}
          data-testid="type"
        />
      </Space>
    </TEInput.LabelWrapper>
  );
};

export default RelativeTime;

const intervalOptions = Object.keys(ERelativeTimeTypes).map((key) => ({
  value: key,
  label: _.capitalize(key),
}));

const intervalMap = {
  [ERelativeTimeTypes.DAYS]: ONE_DAY,
  [ERelativeTimeTypes.HOURS]: ONE_HOUR,
  [ERelativeTimeTypes.MINUTES]: ONE_MINUTE,
};
